import { useEffect, useState } from "react";
import api, { formatDateTime } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, CalendarDays, Trash2, Pencil, Search } from "lucide-react";
import { ConfirmDialog } from "../../components/common";
import { toast } from "sonner";

export default function Appointments() {
  const [list, setList] = useState([]);
  const [techs, setTechs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [techF, setTechF] = useState("all");
  const [dateF, setDateF] = useState("");
  const [del, setDel] = useState(null);
  const [form, setForm] = useState({ title: "", customer_id: "", technician_id: "", start_at: "", end_at: "", notes: "" });

  const load = async () => {
    const [a, t, c] = await Promise.all([api.get("/appointments"), api.get("/technicians"), api.get("/customers")]);
    setList(a.data); setTechs(t.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const EMPTY = { title: "", customer_id: "", technician_id: "", start_at: "", end_at: "", notes: "" };
  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditId(a.id);
    setForm({ title: a.title || "", customer_id: a.customer_id || "", technician_id: a.technician_id || "",
      start_at: (a.start_at || "").slice(0, 16), end_at: (a.end_at || "").slice(0, 16), notes: a.notes || "" });
    setOpen(true);
  };
  const save = async () => {
    if (!form.title || !form.start_at) { toast.error("Başlık ve başlangıç zorunlu"); return; }
    try {
      if (editId) await api.put(`/appointments/${editId}`, form);
      else await api.post("/appointments", form);
      toast.success(editId ? "Randevu güncellendi" : "Randevu eklendi");
      setOpen(false); setForm(EMPTY);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  const remove = async () => { await api.delete(`/appointments/${del.id}`); setDel(null); toast.success("Randevu silindi"); load(); };
  const filtered = list.filter((a) => {
    if (techF !== "all" && a.technician_id !== techF) return false;
    if (dateF && !(a.start_at || "").startsWith(dateF)) return false;
    if (q && !`${a.title} ${a.customer_name || ""} ${a.notes || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  // Group by date
  const grouped = filtered.reduce((acc, a) => {
    const d = a.start_at?.slice(0, 10) || "—";
    if (!acc[d]) acc[d] = [];
    acc[d].push(a);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Takvim</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Randevular</h1>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="new-appointment-button"><Plus className="h-4 w-4 mr-2" /> Yeni Randevu</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Randevuyu Düzenle" : "Yeni Randevu"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Başlık *</Label><Input data-testid="apt-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Müşteri</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Teknisyen</Label>
                  <Select value={form.technician_id} onValueChange={(v) => setForm({ ...form, technician_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent>{techs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Başlangıç *</Label><Input type="datetime-local" data-testid="apt-start-input" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></div>
                <div><Label>Bitiş</Label><Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></div>
              </div>
              <div><Label>Notlar</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border border-slate-200 shadow-none flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Başlık, müşteri veya not ara..." className="pl-9" data-testid="apt-search" />
        </div>
        <Select value={techF} onValueChange={setTechF}>
          <SelectTrigger className="w-48" data-testid="apt-tech-filter"><SelectValue placeholder="Usta" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm ustalar</SelectItem>{techs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={dateF} onChange={(e) => setDateF(e.target.value)} className="w-44" data-testid="apt-date-filter" />
        {(q || dateF || techF !== "all") && <Button variant="ghost" onClick={() => { setQ(""); setDateF(""); setTechF("all"); }}>Temizle</Button>}
      </Card>

      {Object.keys(grouped).length === 0 ? (
        <Card className="p-12 text-center border border-slate-200 shadow-none">
          <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500">Henüz randevu yok</div>
        </Card>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([date, items]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px bg-slate-200 flex-1" />
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">
                  {new Date(date).toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "long" })}
                </div>
                <div className="h-px bg-slate-200 flex-1" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map(a => (
                  <Card key={a.id} className="p-4 border border-slate-200 shadow-none card-lift">
                    <div className="font-semibold text-slate-900">{a.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{formatDateTime(a.start_at)}</div>
                    {a.customer_name && <div className="text-xs text-slate-500 mt-1">👤 {a.customer_name}</div>}
                    {a.technician_name && <div className="text-xs text-slate-500">🔧 {a.technician_name}</div>}
                    {a.notes && <div className="text-xs text-slate-500 mt-2 italic">{a.notes}</div>}
                    <div className="mt-2 flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7" onClick={() => openEdit(a)} data-testid={`edit-apt-${a.id}`}><Pencil className="h-3 w-3 mr-1" /> Düzenle</Button>
                      <Button variant="ghost" size="sm" className="text-rose-600 h-7" onClick={() => setDel(a)} data-testid={`delete-apt-${a.id}`}><Trash2 className="h-3 w-3 mr-1" /> Sil</Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Randevu silinsin mi?" description={del?.title} onConfirm={remove} />
    </div>
  );
}
